import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Checkbox, Field, Modal, PlusIcon, Select, useToast } from "@signet/ui";
import type { CreatorPhotos } from "@signet/web-kit";
import { TypeBadge } from "./cards.tsx";
import { addStock, fetchPhonesOf, phoneLabel, stockKeys, TYPE_LABEL, type AccountType, type Phone, type Poster } from "./api.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Add account (Jay, 2026-10-21): one account typed in by hand, straight onto
   a phone, for accounts that never came in a supplier's file.

   Whose phone works as in Take and Assign: a poster, or a manager who is a
   poster, adds only to their own phones and is never shown a poster menu;
   other managers choose the poster first. The server holds the same rule.
   The account takes the phone's type; a phone with no type yet asks for one.
   ────────────────────────────────────────────────────────────────────────── */

type Typed = Exclude<AccountType, "unmarked">;

export function AddAccount(props: { posters: Poster[]; manages: boolean; me: string; myPhones: Phone[]; creators: string[]; rosterKnown: boolean; photos: CreatorPhotos }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="secondary-button igs-create-phone" onClick={() => setOpen(true)}>
        <PlusIcon size={14} /> Add account
      </button>
      {open ? <AddAccountModal {...props} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function AddAccountModal({ posters, manages, me, myPhones, creators, rosterKnown, photos, onClose }: {
  posters: Poster[]; manages: boolean; me: string; myPhones: Phone[]; creators: string[]; rosterKnown: boolean; photos: CreatorPhotos; onClose: () => void;
}) {
  const client = useQueryClient();
  const toast = useToast();
  const pick = manages && !posters.some((p) => p.id === me);
  const [poster, setPoster] = useState<string | null>(pick ? null : me);
  const theirs = useQuery({ queryKey: [...stockKeys.all, "phones-of", poster], queryFn: () => fetchPhonesOf(poster!), enabled: pick && Boolean(poster) });
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [twoFactor, setTwoFactor] = useState("");
  const [phone, setPhone] = useState<string | null>(null);
  const [newPhones, setNewPhones] = useState<string[]>([]);
  const [type, setType] = useState<Typed | null>(null);
  const [creator, setCreator] = useState<string | null>(null);
  const [warmup, setWarmup] = useState(true);

  const phones = pick ? theirs.data?.phones ?? [] : myPhones;
  const chosen = phones.find((p) => p.label === phone);
  // A new phone, or an empty one from Create phone, has no type until this account gives it one.
  const fixed: Typed | null = chosen && chosen.accountType !== "unmarked" ? chosen.accountType : null;
  const needsType = Boolean(phone) && !fixed;
  const name = username.trim().replace(/^@/, "");
  const nameOk = /^[A-Za-z0-9._]{1,30}$/.test(name);
  const whose = pick ? posters.find((p) => p.id === poster)?.name ?? "their" : "your";

  const save = useMutation({
    mutationFn: () =>
      addStock({
        username: name, password: password || null, email: email.trim() || null, emailPassword: emailPassword || null, twoFactor: twoFactor.trim() || null,
        phone: phone!, creator: creator!, ...(pick ? { poster: poster! } : {}), accountType: fixed ?? type, warmup,
      }),
    onSuccess: (made) => {
      void client.invalidateQueries({ queryKey: stockKeys.all });
      const where = `@${name.toLowerCase()} is on ${pick ? `${whose}'s` : "your"} phone ${made.phone}`;
      if (made.takenBackFrom) toast.success(where, `It was ${made.takenBackFrom}'s, who has left. It's ${creator}'s now, with its progress kept and no link.`);
      else toast.success(where, warmup ? "Warm-up day 1 is next." : "No warm-up: posting day 1 is next.");
      onClose();
    },
    onError: (e) => toast.error("Couldn't add the account", e instanceof Error ? e.message : undefined),
  });

  const ready = nameOk && Boolean(poster && phone && creator) && (!needsType || type) && !save.isPending;
  const phoneOptions = [
    ...phones
      .filter((p) => p.accountType !== "unmarked" || p.accounts === 0)
      .map((p) => ({ value: p.label, label: `Phone ${p.label}`, hint: `${p.accounts} live`, keywords: TYPE_LABEL[p.accountType], badge: <TypeBadge type={p.accountType} mini /> })),
    ...newPhones.filter((l) => !phones.some((p) => p.label === l)).map((l) => ({ value: l, label: `Phone ${l}`, hint: "New phone" })),
  ];
  const creatorOptions = [...new Set(creators.map((c) => c.trim().toUpperCase()))].sort().map((c) => ({ value: c, label: c, image: photos(c) }));

  return (
    <Modal
      open
      onClose={onClose}
      title="Add account"
      description="Type in an account that didn't come in a supplier's file. It goes straight onto the phone."
      footer={
        <>
          <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
          <button type="button" className="button brass" disabled={!ready} onClick={() => save.mutate()}>
            {save.isPending ? "Adding…" : "Add account"}
          </button>
        </>
      }
    >
      <form
        className="igs-take"
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) save.mutate();
        }}
      >
        <Field label="Username" error={name && !nameOk ? "Letters, numbers, dots and underscores, up to 30." : undefined}>
          <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="e.g. annie.daily" autoComplete="off" spellCheck={false} autoFocus />
        </Field>
        <div className="igs-add-pair">
          <Field label="Password">
            <input value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" spellCheck={false} />
          </Field>
          <Field label="2FA key (optional)">
            <input value={twoFactor} onChange={(e) => setTwoFactor(e.target.value)} autoComplete="off" spellCheck={false} />
          </Field>
        </div>
        <div className="igs-add-pair">
          <Field label="Email">
            <input value={email} onChange={(e) => setEmail(e.target.value)} inputMode="email" autoComplete="off" spellCheck={false} />
          </Field>
          <Field label="Email password">
            <input value={emailPassword} onChange={(e) => setEmailPassword(e.target.value)} autoComplete="off" spellCheck={false} />
          </Field>
        </div>
        {pick ? (
          <Field label="Poster">
            <Select
              label="Poster"
              value={poster}
              options={posters.map((p) => ({ value: p.id, label: p.name }))}
              onChange={(next) => {
                setPoster(next);
                setPhone(null);
              }}
              placeholder={posters.length ? "Choose whose phone it goes on" : "Tick posters with the cog beside the switcher first"}
              searchable
            />
          </Field>
        ) : null}
        <Field label="Phone" hint={pick && !poster ? "Choose the poster first." : "Type a new phone's number to add it."}>
          <Select
            label="Phone"
            value={phone}
            options={phoneOptions}
            onChange={setPhone}
            placeholder="Choose or add a phone"
            searchable
            disabled={pick && !poster}
            onCreate={(typed) => {
              const label = phoneLabel(typed);
              if (!/^\d{1,6}$/.test(label)) return;
              setNewPhones((all) => [...new Set([...all, label])]);
              setPhone(label);
            }}
            createLabel={(typed) => `Add phone ${phoneLabel(typed)}`}
          />
        </Field>
        {needsType ? (
          <Field label="Account type" hint={`Phone ${phone} has no type yet. This account sets it.`}>
            <div className="igs-type-switch" role="radiogroup" aria-label="Account type">
              {(["aged", "soft_reg"] as const).map((t) => (
                <button key={t} type="button" role="radio" aria-checked={type === t} className={`igs-type-tab ${t === "aged" ? "aged" : "soft"}${type === t ? " on" : ""}`} onClick={() => setType(t)}>
                  {TYPE_LABEL[t]}
                </button>
              ))}
            </div>
          </Field>
        ) : null}
        <Field label="Creator" hint={rosterKnown ? undefined : "Couldn't read the creators from the Creators tab. Try again in a minute."}>
          <Select label="Creator" value={creator} options={creatorOptions} onChange={setCreator} placeholder="Choose a creator" searchable emptyLabel="No active creator matches that." />
        </Field>
        <label className="igs-add-check">
          <Checkbox checked={warmup} onChange={setWarmup} label="Needs warm-up" />
          Needs warm-up
        </label>
        <p className="faint igs-add-hint">{warmup ? "It starts on warm-up day 1." : "The warm-up is skipped: it starts on posting day 1."}</p>
      </form>
    </Modal>
  );
}
